import { useState } from "react";
import { useTranslation } from "react-i18next";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Copy, Edit, Key, Plus, Trash2, Eye, EyeOff } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import {
  useMyApiKeysQuery,
  useCreateMyApiKeyMutation,
  useUpdateMyApiKeyMutation,
  useDeleteMyApiKeyMutation
} from "@/generated/graphql";
import { formatDateTime } from "@/lib/format";

const ApiKeys = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingKey, setEditingKey] = useState<{ id: string; name: string } | null>(null);
  const [deletingKeyId, setDeletingKeyId] = useState<string | null>(null);
  const [newKeyName, setNewKeyName] = useState("");
  const [editKeyName, setEditKeyName] = useState("");
  const [visibleKeys, setVisibleKeys] = useState<Set<string>>(new Set());

  const { data, loading, error, refetch } = useMyApiKeysQuery();
  const [createApiKey, { loading: creating }] = useCreateMyApiKeyMutation();
  const [updateApiKey, { loading: updating }] = useUpdateMyApiKeyMutation();
  const [deleteApiKey, { loading: deleting }] = useDeleteMyApiKeyMutation();

  const apiKeys = data?.myAPIKeys || [];

  const handleCreateApiKey = async () => {
    if (!newKeyName.trim()) {
      toast({
        title: t('apiKeys.toast.error'),
        description: t('apiKeys.toast.keyNameRequired'),
        variant: "destructive",
      });
      return;
    }

    try {
      const result = await createApiKey({
        variables: {
          input: {
            name: newKeyName.trim(),
          },
        },
      });

      if (result.data?.createMyAPIKey) {
        toast({
          title: t('apiKeys.toast.success'),
          description: t('apiKeys.toast.keyCreated'),
        });
        setNewKeyName("");
        setShowCreateModal(false);
        refetch();
      }
    } catch (err) {
      console.error(err);
      toast({
        title: t('apiKeys.toast.error'),
        description: t('apiKeys.toast.keyCreateError'),
        variant: "destructive",
      });
    }
  };

  const handleUpdateApiKey = async () => {
    if (!editingKey || !editKeyName.trim()) {
      toast({
        title: t('apiKeys.toast.error'),
        description: t('apiKeys.toast.keyNameRequired'),
        variant: "destructive",
      });
      return;
    }

    try {
      await updateApiKey({
        variables: {
          id: editingKey.id,
          input: {
            name: editKeyName.trim(),
          },
        },
      });

      toast({
        title: t('apiKeys.toast.success'),
        description: t('apiKeys.toast.keyUpdated'),
      });
      setEditingKey(null);
      setEditKeyName("");
      refetch();
    } catch (err) {
      console.error(err);
      toast({
        title: t('apiKeys.toast.error'),
        description: t('apiKeys.toast.keyUpdateError'),
        variant: "destructive",
      });
    }
  };

  const handleDeleteApiKey = async (id: string) => {
    try {
      await deleteApiKey({
        variables: { id },
      });

      toast({
        title: t('apiKeys.toast.success'),
        description: t('apiKeys.toast.keyDeleted'),
      });
      setDeletingKeyId(null);
      refetch();
    } catch (err) {
      console.error(err);
      toast({
        title: t('apiKeys.toast.error'),
        description: t('apiKeys.toast.keyDeleteError'),
        variant: "destructive",
      });
    }
  };

  const handleCopyKey = async (key: string) => {
    try {
      await navigator.clipboard.writeText(key);
      toast({
        title: t('apiKeys.toast.copied'),
        description: t('apiKeys.toast.keyCopied'),
      });
    } catch (err) {
      console.error(err);
      toast({
        title: t('apiKeys.toast.error'),
        description: t('apiKeys.toast.copyError'),
        variant: "destructive",
      });
    }
  };

  const toggleKeyVisibility = (keyId: string) => {
    setVisibleKeys(prev => {
      const newSet = new Set(prev);
      if (newSet.has(keyId)) {
        newSet.delete(keyId);
      } else {
        newSet.add(keyId);
      }
      return newSet;
    });
  };

  const maskKey = (key: string) => key.replace(/./g, '*');

  return (
    <Layout>
      <div className="flex-1 p-4 lg:p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl lg:text-2xl font-bold">{t('apiKeys.title')}</h2>
            <p className="text-sm text-muted-foreground mt-1">
              {t('apiKeys.subtitle')}
            </p>
          </div>

          <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
            <DialogTrigger asChild>
              <Button className="gap-2">
                <Plus className="w-4 h-4" />
                {t('apiKeys.newKey')}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{t('apiKeys.createNewApiKey')}</DialogTitle>
                <DialogDescription>
                  {t('apiKeys.createNewApiKeyDesc')}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="keyName">{t('apiKeys.keyName')}</Label>
                  <Input
                    id="keyName"
                    placeholder={t('apiKeys.keyNamePlaceholder')}
                    value={newKeyName}
                    className="mt-2"
                    autoFocus
                    onChange={(e) => setNewKeyName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleCreateApiKey();
                      }
                    }}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewKeyName("");
                  }}
                >
                  {t('apiKeys.cancel')}
                </Button>
                <Button
                  onClick={handleCreateApiKey}
                  disabled={creating || !newKeyName.trim()}
                >
                  {creating ? t('apiKeys.creating') : t('apiKeys.createKey')}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {loading && (
          <div className="grid grid-cols-1 gap-4">
            {Array.from({ length: 3 }).map((_, idx) => (
              <Card key={`skeleton-${idx}`} className="animate-pulse">
                <CardHeader>
                  <div className="h-4 bg-muted rounded w-1/3" />
                  <div className="h-3 bg-muted rounded w-1/4" />
                </CardHeader>
                <CardContent>
                  <div className="h-8 bg-muted rounded w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {!loading && error && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center text-destructive">
                {t('apiKeys.failedToLoad')}
              </div>
            </CardContent>
          </Card>
        )}

        {!loading && !error && (
          <div className="space-y-4">
            {apiKeys.length === 0 ? (
              <Card>
                <CardContent className="pt-6">
                  <div className="text-center">
                    <Key className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium mb-2">{t('apiKeys.noApiKeys')}</h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      {t('apiKeys.noApiKeysDesc')}
                    </p>
                    <Button
                      onClick={() => setShowCreateModal(true)}
                      className="gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      {t('apiKeys.createFirstKey')}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              apiKeys.map((apiKey) => (
                <Card key={apiKey.id}>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg">{apiKey.name}</CardTitle>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            {t('apiKeys.createdAt')} {formatDateTime(apiKey.createdAt)}
                          </Badge>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setEditingKey({ id: apiKey.id, name: apiKey.name });
                            setEditKeyName(apiKey.name);
                          }}
                          className="gap-2"
                        >
                          <Edit className="w-4 h-4" />
                          {t('apiKeys.edit')}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setDeletingKeyId(apiKey.id)}
                          className="gap-2 text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                          {t('apiKeys.delete')}
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-sm font-medium">{t('apiKeys.apiKey')}</Label>
                        <div className="flex items-center gap-2 mt-1">
                          <div className="flex-1 p-2 bg-muted rounded border font-mono text-sm">
                            {visibleKeys.has(apiKey.id) ? apiKey.key : maskKey(apiKey.key)}
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleKeyVisibility(apiKey.id)}
                          >
                            {visibleKeys.has(apiKey.id) ? (
                              <EyeOff className="w-4 h-4" />
                            ) : (
                              <Eye className="w-4 h-4" />
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCopyKey(apiKey.key)}
                            className="gap-2"
                          >
                            <Copy className="w-4 h-4" />
                            {t('apiKeys.copy')}
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Edit Key Dialog */}
        <Dialog
          open={!!editingKey}
          onOpenChange={(open) => {
            if (!open) {
              setEditingKey(null);
              setEditKeyName("");
            }
          }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('apiKeys.editApiKey')}</DialogTitle>
              <DialogDescription>
                {t('apiKeys.editApiKeyDesc')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="editKeyName">{t('apiKeys.keyName')}</Label>
                <Input
                  id="editKeyName"
                  placeholder={t('apiKeys.keyNamePlaceholder')}
                  value={editKeyName}
                  autoFocus
                  className="mt-2"
                  onChange={(e) => setEditKeyName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleUpdateApiKey();
                    }
                  }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => {
                  setEditingKey(null);
                  setEditKeyName("");
                }}
              >
                {t('apiKeys.cancel')}
              </Button>
              <Button
                onClick={handleUpdateApiKey}
                disabled={updating || !editKeyName.trim()}
              >
                {updating ? t('apiKeys.saving') : t('apiKeys.saveChanges')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog
          open={!!deletingKeyId}
          onOpenChange={(open) => {
            if (!open) setDeletingKeyId(null);
          }}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{t('apiKeys.deleteApiKey')}</AlertDialogTitle>
              <AlertDialogDescription>
                {t('apiKeys.deleteApiKeyDesc')}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('apiKeys.cancel')}</AlertDialogCancel>
              <AlertDialogAction
                onClick={() => deletingKeyId && handleDeleteApiKey(deletingKeyId)}
                disabled={deleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {deleting ? t('apiKeys.deleting') : t('apiKeys.delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default ApiKeys;
